import { PaginatorState } from "src/app/shared/components/treeslo-grid/treeslo-paginator-cursor-based/treeslo-paginator-cursor-based.component";
import { TreesloGridSortSpec } from "src/app/shared/components/treeslo-grid/treeslo-grid.model";
import { serializeSortForQuery } from "src/app/shared/components/treeslo-grid/helpers/treeslo-grid-sort.helper";
import { StoreService } from "src/app/services/apps/store/store.service";
import { CLAIM_MUTABLE_STATUS } from "../claims/claim-mutation.helper";
import { StoreGetDTO } from "../stores/store";

export const DEFAULT_CLAIM_LIST_PAGE_SIZE = 15;
export const CLAIM_LIST_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30];

export interface ClaimListFilter {
  provider: string;
  storeId: string;
  claimType: string;
  status: string;
  startFiledDate: string;
  endFiledDate: string;
}

export interface ClaimListQueryInput {
  filter: ClaimListFilter;
  pageSize: number;
  cursor: string | null;
  stores: StoreGetDTO[];
  applySingleStoreDefault: boolean;
  sort?: TreesloGridSortSpec[];
  formatDate?: (value: Date | string) => string;
}

export function defaultClaimListFilter(
  startDate: string,
  endDate: string,
): ClaimListFilter {
  return {
    provider: "",
    storeId: "",
    claimType: "",
    status: CLAIM_MUTABLE_STATUS,
    startFiledDate: startDate,
    endFiledDate: endDate,
  };
}

export function buildClaimListQuery(
  input: ClaimListQueryInput,
): Record<string, string> {
  const formatDate =
    input.formatDate ??
    ((value: Date | string) =>
      typeof value === "string" ? value : value.toISOString().slice(0, 10));

  const storeId = StoreService.resolveStoreIdWithSingleStoreDefault(
    input.filter.storeId,
    input.stores,
    input.applySingleStoreDefault,
  );

  const params: Record<string, string> = {
    limit: input.pageSize.toString(),
  };

  if (input.filter.provider) {
    params["provider"] = input.filter.provider;
  }
  if (storeId) {
    params["storeId"] = storeId;
  }
  if (input.filter.claimType) {
    params["claimType"] = input.filter.claimType;
  }
  if (input.filter.status) {
    params["status"] = input.filter.status.toUpperCase();
  }

  const startDate = input.filter.startFiledDate
    ? formatDate(input.filter.startFiledDate)
    : "";
  const endDate = input.filter.endFiledDate
    ? formatDate(input.filter.endFiledDate)
    : "";

  if (startDate) {
    params["startDate"] = startDate;
  }
  if (endDate) {
    params["endDate"] = endDate;
  }
  if (input.cursor) {
    params["cursor"] = input.cursor;
  }

  const serializedSort = serializeSortForQuery(input.sort ?? []);
  if (serializedSort) {
    params["sort"] = serializedSort;
  }

  return params;
}

export function buildClaimListQueryFromRouteContext(
  contextData:
    | {
        filterForm?: ClaimListFilter;
        paginatorState?: PaginatorState;
        sort?: TreesloGridSortSpec[];
      }
    | undefined,
  stores: StoreGetDTO[],
  defaultRange: { startDate: string; endDate: string },
): Record<string, string> {
  const filter =
    contextData?.filterForm ??
    defaultClaimListFilter(defaultRange.startDate, defaultRange.endDate);

  const paginator: PaginatorState = contextData?.paginatorState ?? {
    pageIndex: 0,
    pageSize: DEFAULT_CLAIM_LIST_PAGE_SIZE,
    cursors: [null],
  };

  const cursor = paginator.cursors[paginator.pageIndex] ?? null;

  return buildClaimListQuery({
    filter,
    pageSize: paginator.pageSize || DEFAULT_CLAIM_LIST_PAGE_SIZE,
    cursor,
    stores,
    applySingleStoreDefault: !contextData?.filterForm,
    sort: contextData?.sort,
  });
}
